import React, { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Search, CreditCard, Calendar, ArrowLeft } from "lucide-react";
import { API } from "@/App";
import { Link } from "react-router-dom";

const GiftCardBalancePage = () => {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const checkBalance = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch(`${API}/gift-cards/check/${code.trim()}`);
      if (!res.ok) {
        setError("Gavekort ikke funnet. Sjekk koden og prøv igjen.");
        return;
      }
      setResult(await res.json());
    } catch {
      setError("Noe gikk galt. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("nb-NO", { day: "numeric", month: "long", year: "numeric" });
    } catch { return d; }
  };

  const balancePercent = result ? Math.round((result.current_balance / result.initial_balance) * 100) : 0;

  return (
    <div
      className="min-h-screen pt-24 pb-16"
      style={{ background: "linear-gradient(180deg, #1a0805 0%, #0d0402 100%)" }}
    >
      <div className="max-w-lg mx-auto px-4">
        {/* Back link */}
        <Link
          to="/gift-cards"
          className="inline-flex items-center gap-2 mb-8 text-sm hover:underline"
          style={{ color: "#d4a847" }}
          data-testid="back-to-giftcards"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbake til gavekort
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Gift className="h-10 w-10 mx-auto mb-3" style={{ color: "#d4a847" }} />
          <h1
            className="text-3xl font-serif italic font-bold"
            style={{ color: "#e8c86a" }}
          >
            Sjekk gavekort-saldo
          </h1>
          <p className="mt-2 text-sm" style={{ color: "#b8a080" }}>
            Skriv inn gavekort-koden din for å se gjenstående saldo
          </p>
        </motion.div>

        {/* Search form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={checkBalance}
          className="rounded-xl p-6"
          style={{
            background: "rgba(45,24,16,0.6)",
            border: "1.5px solid rgba(212,168,71,0.3)",
          }}
          data-testid="balance-check-form"
        >
          <label
            className="block text-sm font-semibold mb-2"
            style={{ color: "#d4a847" }}
          >
            Gavekort-kode
          </label>
          <div className="flex gap-3">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="GC-XXXXXXXXXXXX"
              className="flex-1 px-4 py-3 rounded-lg text-sm font-mono tracking-wider"
              style={{
                background: "rgba(0,0,0,0.3)",
                border: "1px solid rgba(212,168,71,0.3)",
                color: "#fff8e7",
                outline: "none",
              }}
              data-testid="balance-code-input"
            />
            <button
              type="submit"
              disabled={loading || !code.trim()}
              className="px-5 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 transition-all hover:brightness-110 disabled:opacity-50"
              style={{
                background: "linear-gradient(135deg, #d4a847, #a08030)",
                color: "#1a0805",
              }}
              data-testid="balance-check-btn"
            >
              <Search className="h-4 w-4" />
              {loading ? "Søker..." : "Sjekk"}
            </button>
          </div>
        </motion.form>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-4 p-4 rounded-lg text-center text-sm"
            style={{
              background: "rgba(200,40,40,0.15)",
              border: "1px solid rgba(200,40,40,0.3)",
              color: "#ff8080",
            }}
            data-testid="balance-error"
          >
            {error}
          </motion.div>
        )}

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 rounded-xl overflow-hidden"
            style={{
              border: "2px solid #d4a847",
              background: "rgba(26,8,5,0.9)",
            }}
            data-testid="balance-result"
          >
            {/* Balance header */}
            <div className="p-6 text-center" style={{ borderBottom: "1px solid rgba(212,168,71,0.2)" }}>
              <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "#a08030" }}>
                Gjenstående saldo
              </p>
              <p className="text-4xl font-bold" style={{ color: result.current_balance > 0 ? "#22c55e" : "#ff6b6b" }}>
                kr {result.current_balance},-
              </p>

              {/* Progress bar */}
              <div className="mt-4 mx-auto max-w-xs">
                <div
                  className="h-2 rounded-full overflow-hidden"
                  style={{ background: "rgba(212,168,71,0.15)" }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${balancePercent}%`,
                      background: balancePercent > 50 ? "#22c55e" : balancePercent > 20 ? "#f59e0b" : "#ef4444",
                    }}
                  />
                </div>
                <p className="text-xs mt-1" style={{ color: "#8b7355" }}>
                  {balancePercent}% av kr {result.initial_balance},- gjenstår
                </p>
              </div>
            </div>

            {/* Details */}
            <div className="p-5 space-y-3">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-sm" style={{ color: "#a08030" }}>
                  <CreditCard className="h-4 w-4" /> Kode
                </span>
                <span className="font-mono font-bold text-sm" style={{ color: "#e8c86a" }}>
                  {result.card_code}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-sm" style={{ color: "#a08030" }}>
                  <Gift className="h-4 w-4" /> Opprinnelig beløp
                </span>
                <span className="font-semibold text-sm" style={{ color: "#fff8e7" }}>
                  kr {result.initial_balance},-
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-sm" style={{ color: "#a08030" }}>
                  <Calendar className="h-4 w-4" /> Kjøpt
                </span>
                <span className="text-sm" style={{ color: "#fff8e7" }}>
                  {formatDate(result.created_at)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-sm" style={{ color: "#a08030" }}>
                  <Calendar className="h-4 w-4" /> Utløper
                </span>
                <span className="text-sm" style={{ color: "#fff8e7" }}>
                  {formatDate(result.expires_at)}
                </span>
              </div>
            </div>

            {/* Use it CTA */}
            <div className="px-5 pb-5">
              <Link
                to="/order"
                className="block text-center py-3 rounded-lg font-semibold text-sm transition-all hover:brightness-110"
                style={{
                  background: "linear-gradient(135deg, #d4a847, #a08030)",
                  color: "#1a0805",
                }}
                data-testid="use-giftcard-btn"
              >
                Bruk gavekortet — Bestill nå
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default GiftCardBalancePage;
